import { useEffect, useState } from "react"
import { DetailPage, Row } from "../../components/crud/DetailPage"
import { Badge, FkLink, ListPage } from "../../components/crud/ListPage"
import { AsyncSelect, Checkbox, DateInput, FileInput, NumberInput, Select, TextInput, toDateOnly } from "../../components/forms/Inputs"
import { useOptions } from "../../hooks/useOptions"
import { useTranslations } from "../../layers/Localization"
import { FacturesDetailsList, getTotalsFactureDetail } from "./FacturesDetail"
import { useApi } from "../../hooks/useApi"
import { useNavigate, useParams } from "react-router-dom"
import { TvaInput, useRegimeTvaOptions } from "./Clients"
import { Currency, toCurrency } from "../../hooks/helpers"
import { Loading, Popup } from "../../components/layout/Popup"
import { useObjectState } from "../../hooks/useObjectState"
import { Form } from "../../components/forms/Form"
import { Button } from "../../components/layout/Button"
import { FaBan, FaFileImport, FaPaperPlane, FaSave } from "react-icons/fa"
import { useToday } from "../../hooks/useToday"
import { useLangueOptions } from "./Gerants"
import { useCodesTvaOptions } from "./Articles"

export const useAVerifierOptions = () => {
    const t = useTranslations()
    return [
        { value: true, label: t('A vérifier'), color: '#dc2626' },
        { value: false, label: null, color: null },
    ]
}

export const useStatusEnvoiEmailOptions = () => {
    const t = useTranslations()
    return [
        { value: 'NON_ENVOYE', label: t('Non envoyé'), color: '#9ca3af' },
        { value: 'ENVOYE', label: t('Envoyé'), color: '#16a34a' },
        { value: 'ECHOUE', label: t('Échoué'), color: '#dc2626' },
    ]
}

const useActions = (setFacture, setPopup, setReload) => {
    const t = useTranslations()
    const api = useApi()
    const today = useToday()
    return {
        [t('Aperçu facture')]: x => window.open(`${process.env.REACT_APP_API_URL}/factures/${x.id}/pdf`, '_blank').focus(),
        [t('Finaliser proforma')]: (x, reload) => api.post(`/factures/${x.id}/convert-draft`, { date: today }).then(reload),
        [t('Envoyer email')]: (x, reload) => {
            setFacture(x)
            setPopup(true)
            setReload(() => reload)
        },
    }
}

export const getTotalFacture = (item, codesTva, detailKey = 'facturesDetail') => {
    const totalHtva = item[detailKey].map(x => getTotalsFactureDetail(x, codesTva)).reduce((total, x) => total + x.totalHtva, 0)
    const totalTva = item[detailKey].map(x => getTotalsFactureDetail(x, codesTva)).reduce((total, x) => total + x.totalTva, 0)
    const totalTtc = item[detailKey].map(x => getTotalsFactureDetail(x, codesTva)).reduce((total, x) => total + x.totalTtc, 0)
    return { totalHtva, totalTva, totalTtc }
}

function FactureFields({ item, set, tab, originalItem }) {
    const [client, setClient] = useState()
    const [details, setDetails] = useState([])
    const codesTvaOptions = useCodesTvaOptions()
    const t = useTranslations()
    const clientOptions = useOptions('clients', ['num', 'referenceCourte'])
    const statusEnvoiEmailOptions = useStatusEnvoiEmailOptions()
    const api = useApi()
    const { id } = useParams()
    const langueOptions = useLangueOptions()
    const regimeTvaOptions = useRegimeTvaOptions()

    useEffect(() => {
        if (id === 'new') api.get(`/factures?limit=1&orderBy:desc=num&draft=true`).then(invoices => set('num')((invoices[0]?.num || 0) + 1))
        else {
            api.get(`/facturesDetail?factureId=${encodeURIComponent(id)}&limit=100`).then(setDetails)
        }
    }, [id])

    useEffect(() => {
        if (!item.num || !item.clientId) set('codeStructure')(null)
        else if (client) {
            const invoiceNum = String(item.num).padStart(5, '0')
            const clientNum = String(client.num).padStart(5, '0')
            set('codeStructure')(`+++${invoiceNum.slice(0, 3)}/${invoiceNum.slice(3)}${clientNum.slice(0, 2)}/${clientNum.slice(2)}${String(Number(invoiceNum + clientNum) % 97 || 97).padStart(2, '0')}+++`)
        }
    }, [item.num, client])

    const onDateChange = value => {
        set('date')(value)
        set('dateEcheance')(value ? new Date(Date.UTC(value.getFullYear(), Number(value.getMonth()) + 1, 15)) : null)
        set('exercice')(value ? value.getUTCFullYear() : null)
    }
    const onClientChange = (value, item) => {
        set('clientId')(value)
        set('clientNum')(item ? item.num : null)
        set('clientNom')(item ? item.nom : null)
        set('clientNumTva')(item ? item.numTva : null)
        set('clientAdresse')(item ? item.adresse : null)
        set('clientCp')(item ? item.cp : null)
        set('clientCommune')(item ? item.commune : null)
        set('clientRegimeTva')(item ? item.regimeTva : null)
        set('clientLangue')(item ? item.langue : null)
    }

    useEffect(() => {
        if (item.date && !item.dateEcheance) set('dateEcheance')(new Date(Date.UTC(item.date.getFullYear(), Number(item.date.getMonth()) + 1, 15)))
        if (item.date && !item.exercice) set('exercice')(item.date.getUTCFullYear())
    }, [id])

    return <>
        <Row>
            <Select label={t("Email")} value={item.statusEnvoiEmail} onChange={set('statusEnvoiEmail')} options={statusEnvoiEmailOptions} required disabled={originalItem.draft} />
            <Checkbox center className="max-w-sm" label={t("A vérifier")} value={item.aVerifier} onChange={set('aVerifier')} help={t("Automatiquement activé pour les factures de premier ou dernier travail ou si le chantier est marqué comme à vérifier")} />
            <Checkbox center label={t("Encodée en comptabilité")} value={item.comptabilite} onChange={set('comptabilite')} disabled={originalItem.draft} />
        </Row>
        <Row>
            <DateInput label={t("Date aimable rappel")} value={item.datePremierRappel} onChange={set('datePremierRappel')} disabled={originalItem.draft} />
            <DateInput label={t("Date dernier rappel")} value={item.dateSecondRappel} onChange={set('dateSecondRappel')} disabled={originalItem.draft} />
            <Checkbox center label={t("Payée / Créditée")} value={item.payee} onChange={set('payee')} disabled={originalItem.draft} />
        </Row>
        <hr />
        <Row>
            <NumberInput label={t("Numéro")} value={item.num} onChange={set('num')} required max="999999" disabled />
            <NumberInput label={t("Exercice")} value={item.exercice} onChange={set('exercice')} required disabled />
            <DateInput label={t("Date")} value={item.date} onChange={onDateChange} required disabled={!originalItem.draft} />
            <DateInput label={t("Date d'échéance")} value={item.dateEcheance} onChange={set('dateEcheance')} required disabled={originalItem.comptabilite || item.comptabilite} />
        </Row>
        <hr />
        <Row>
            <AsyncSelect className="grow" label={t("Client")} value={item.clientId} onChange={onClientChange} onItemLoad={setClient} options={clientOptions} required disabled={originalItem.comptabilite || item.comptabilite} />
            <TextInput className="grow" label={t("Nom")} value={item.clientNom} onChange={set('clientNom')} required disabled={originalItem.comptabilite || item.comptabilite} />
            <Select label={t("Langue")} value={item.clientLangue} onChange={set('clientLangue')} options={langueOptions} required disabled={originalItem.comptabilite || item.comptabilite} />
        </Row>
        <Row>
            <TvaInput label={t("Numéro de TVA")} value={item.clientNumTva} onChange={set('clientNumTva')} disabled={originalItem.comptabilite || item.comptabilite} />
            <Select label={t("Régime TVA")} value={item.clientRegimeTva} onChange={set('clientRegimeTva')} required options={regimeTvaOptions} disabled={originalItem.comptabilite || item.comptabilite} />
        </Row>
        <Row>
            <TextInput className="grow" label={t("Adresse")} value={item.clientAdresse} onChange={set('clientAdresse')} disabled={originalItem.comptabilite || item.comptabilite} />
            <TextInput label={t("Code postal")} value={item.clientCp} onChange={set('clientCp')} disabled={originalItem.comptabilite || item.comptabilite} />
            <TextInput label={t("Commune")} value={item.clientCommune} onChange={set('clientCommune')} disabled={originalItem.comptabilite || item.comptabilite} />
        </Row>
        <hr />
        <Row>
            <TextInput className="grow" label={t("Communication structurée")} value={item.codeStructure} onChange={set('codeStructure')} pattern="^\+{3}[0-9]{3}/[0-9]{4}/[0-9]{5}\+{3}$" disabled={originalItem.comptabilite || item.comptabilite} />
            <TextInput className="grow" label={t("Objet de la facture")} value={item.objet} onChange={set('objet')} disabled={originalItem.comptabilite || item.comptabilite} />
            <TextInput className="grow" label={t("Note sur facture")} value={item.note} onChange={set('note')} disabled={originalItem.comptabilite || item.comptabilite} />
        </Row>
        {(!!details.length) && <div className="flex">
            <div className={`grid grid-cols-[repeat(6,auto)] gap-x-8 gap-y-3 mt-5 whitespace-nowrap overflow-auto ${originalItem.comptabilite || item.comptabilite ? 'opacity-50' : ''}`}>
                <div></div>
                <div className="font-bold">0%</div>
                <div className="font-bold">6%</div>
                <div className="font-bold">21%</div>
                <div className="font-bold">20 bis</div>
                <div className="font-bold">{t("Totaux:")}</div>
                <div className="font-bold">{t("Bases:")}</div>
                <Currency value={details.filter(x => x.codeTva === 'ZERO').reduce((total, x) => total + getTotalsFactureDetail(x, codesTvaOptions).totalHtva, 0)} />
                <Currency value={details.filter(x => x.codeTva === 'SIX').reduce((total, x) => total + getTotalsFactureDetail(x, codesTvaOptions).totalHtva, 0)} />
                <Currency value={details.filter(x => x.codeTva === 'VINGT_ET_UN').reduce((total, x) => total + getTotalsFactureDetail(x, codesTvaOptions).totalHtva, 0)} />
                <Currency value={details.filter(x => x.codeTva === 'VINGT_BIS').reduce((total, x) => total + getTotalsFactureDetail(x, codesTvaOptions).totalHtva, 0)} />
                <Currency value={details.reduce((total, x) => total + getTotalsFactureDetail(x, codesTvaOptions).totalHtva, 0)} />
                <div className="font-bold">{t("TVA:")}</div>
                <Currency value={details.filter(x => x.codeTva === 'ZERO').reduce((total, x) => total + getTotalsFactureDetail(x, codesTvaOptions).totalTva, 0)} />
                <Currency value={details.filter(x => x.codeTva === 'SIX').reduce((total, x) => total + getTotalsFactureDetail(x, codesTvaOptions).totalTva, 0)} />
                <Currency value={details.filter(x => x.codeTva === 'VINGT_ET_UN').reduce((total, x) => total + getTotalsFactureDetail(x, codesTvaOptions).totalTva, 0)} />
                <Currency value={details.filter(x => x.codeTva === 'VINGT_BIS').reduce((total, x) => total + getTotalsFactureDetail(x, codesTvaOptions).totalTva, 0)} />
                <Currency value={details.reduce((total, x) => total + getTotalsFactureDetail(x, codesTvaOptions).totalTva, 0)} />
                <div className="font-bold">{t("Total:")}</div>
                <Currency value={details.filter(x => x.codeTva === 'ZERO').reduce((total, x) => total + getTotalsFactureDetail(x, codesTvaOptions).totalTtc, 0)} />
                <Currency value={details.filter(x => x.codeTva === 'SIX').reduce((total, x) => total + getTotalsFactureDetail(x, codesTvaOptions).totalTtc, 0)} />
                <Currency value={details.filter(x => x.codeTva === 'VINGT_ET_UN').reduce((total, x) => total + getTotalsFactureDetail(x, codesTvaOptions).totalTtc, 0)} />
                <Currency value={details.filter(x => x.codeTva === 'VINGT_BIS').reduce((total, x) => total + getTotalsFactureDetail(x, codesTvaOptions).totalTtc, 0)} />
                <Currency value={details.reduce((total, x) => total + getTotalsFactureDetail(x, codesTvaOptions).totalTtc, 0)} />
            </div>
        </div>}
    </>
}

export function FacturesList(props) {
    const codesTvaOptions = useCodesTvaOptions()
    const [generatePopup, setGeneratePopup] = useState(false)
    const [emailPopup, setEmailPopup] = useState(false)
    const [singleEmailPopup, setSingleEmailPopup] = useState(false)
    const [singleEmailFacture, setSingleEmailFacture] = useState()
    const [importUnpaidPopup, setImportUnpaidPopup] = useState(false)
    const [sendFirstReminderPopup, setSendFirstReminderPopup] = useState(false)
    const [sendLastReminderPopup, setSendLastReminderPopup] = useState(false)
    const [reload, setReload] = useState()
    const t = useTranslations()
    const aVerifierOptions = useAVerifierOptions()
    const statusEnvoiEmailOptions = useStatusEnvoiEmailOptions()
    const actions = useActions(setSingleEmailFacture, setSingleEmailPopup, setReload)
    const api = useApi()
    const today = useToday()
    const navigate = useNavigate()

    return <>
        <ListPage model="factures" title={t("Factures de vente")} columns={{
            [t('Exercice')]: 'exercice',
            [t('Numéro')]: 'num',
            [t('Total TTC')]: { value: item => <Currency value={getTotalFacture(item, codesTvaOptions).totalTtc} /> },
            [t('Date')]: 'date',
            [t('Client')]: { sort: 'client', value: item => <FkLink>{item.clientNom} ({(item.client?.referenceCourte)})</FkLink> },
            [t('Status')]: { sort: 'aVerifier', value: item => <Badge value={item?.aVerifier} options={aVerifierOptions} /> },
            [t('Email')]: { sort: 'statusEnvoiEmail', value: item => <Badge value={item?.statusEnvoiEmail} options={statusEnvoiEmailOptions} /> },
        }} sort={tab => tab === t('Factures proforma')
            ? { [t('Status')]: 'desc', [t('Exercice')]: 'desc', [t('Numéro')]: 'desc' }
            : { [t('Exercice')]: 'desc', [t('Numéro')]: 'desc' }
        } join={{
            client: ['num', 'nom', 'referenceCourte'],
            facturesDetail: '',
        }} filter={{
            [t('Factures proforma')]: 'draft=true',
            [t('Factures à envoyer')]: 'draft=false&statusEnvoiEmail:not=ENVOYE',
            [t('Factures à encoder')]: 'draft=false&comptabilite=false',
            [t('Factures non payées')]: 'draft=false&payee=false',
            [t('Factures en retard')]: `draft=false&payee=false&dateEcheance:lt=${encodeURIComponent(today.toISOString())}`,
            [t('Aimable rappel envoyé')]: 'draft=false&datePremierRappel:not=&payee=false',
            [t('Dernier rappel envoyé')]: 'draft=false&dateSecondRappel:not=&payee=false',
            [t('Toutes les factures')]: `draft=false`,
        }} actions={actions} mainActions={{
            [t('Supprimer proforma automatiques')]: reload => api.post(`/factures/delete-generated-draft`).then(reload),
            [t('Générer proforma automatiques')]: reload => setGeneratePopup(true),
            [t('Controler facturation')]: reload => navigate('/factures/controle'),
            [t('Finaliser proforma')]: reload => api.post(`/factures/convert-draft`, { date: today }).then(reload),
            [t('Envoyer factures par email')]: reload => {
                setEmailPopup(true)
                setReload(() => reload)
            },
            [t('Exporter factures vers popsy')]: reload => {
                window.open(`${process.env.REACT_APP_API_URL}/factures/export-popsy`)
                setTimeout(reload, 100)
            },
            [t('Importer factures non payées')]: reload => {
                setImportUnpaidPopup(true)
                setReload(() => reload)
            },
            [t('Envoyer premiers rappels')]: reload => {
                setSendFirstReminderPopup(true)
                setReload(() => reload)
            },
            [t('Envoyer derniers rappels')]: reload => {
                setSendLastReminderPopup(true)
                setReload(() => reload)
            },
        }} {...props} />
        <GeneratePopup value={generatePopup} onChange={setGeneratePopup} />
        <SendEmailPopup value={emailPopup} onChange={setEmailPopup} reload={reload} />
        <SendSingleEmailPopup value={singleEmailPopup} onChange={setSingleEmailPopup} model="factures" item={singleEmailFacture} reload={reload} />
        <ImportUnpaidInvoicesPopup value={importUnpaidPopup} onChange={setImportUnpaidPopup} reload={reload} />
        <SendFirstRemindersPopup value={sendFirstReminderPopup} onChange={setSendFirstReminderPopup} reload={reload} />
        <SendLastRemindersPopup value={sendLastReminderPopup} onChange={setSendLastReminderPopup} reload={reload} />
    </>
}
export function FactureDetail(props) {
    const [facture, setFacture] = useState()
    const [singleEmailPopup, setSingleEmailPopup] = useState(false)
    const [reload, setReload] = useState(false)
    const t = useTranslations()
    const actions = useActions(setFacture, setSingleEmailPopup, setReload)
    const today = useToday()

    return <>
        <DetailPage model="factures" title={t("Facture de vente")} Fields={FactureFields} onItemLoad={setFacture} actions={actions} nextKey="num" nextQuery={`draft=${facture?.draft}&exercice=${facture?.exercice}`} defaults={{
            draft: true,
            statusEnvoiEmail: "NON_ENVOYE",
            date: new Date(today),
        }} tables={{
            [t("Détails")]: <FacturesDetailsList relation="factureId" root="." disabled={facture?.comptabilite} />,
            ...(facture?.clientId ? {
                [t("Autres factures du client")]: <FacturesList filter={{
                    [t('Factures proforma')]: `draft=true&clientId=${facture.clientId}`,
                    [t('Factures à envoyer')]: `draft=false&statusEnvoiEmail:not=ENVOYE&clientId=${facture.clientId}`,
                    [t('Factures à encoder')]: `draft=false&comptabilite=false&clientId=${facture.clientId}`,
                    [t('Factures non payées')]: `draft=false&payee=false&clientId=${facture.clientId}`,
                    [t('Factures en retard')]: `draft=false&payee=false&dateEcheance:lt=${encodeURIComponent(today.toISOString())}`,
                    [t('Toutes les factures')]: `draft=false&statusEnvoiEmail=ENVOYE&comptabilite=true&clientId=${facture.clientId}`,
                }} mainActions={undefined} title={undefined} />
            } : {})
        }} {...props} />
        {facture && <SendSingleEmailPopup value={singleEmailPopup} onChange={setSingleEmailPopup} model="factures" item={facture} reload={reload} />}
    </>
}

function GeneratePopup({ value, onChange }) {
    const t = useTranslations()
    const defaultPayload = {
        date: toDateOnly(new Date()),
        dateEcheance: toDateOnly(new Date(Date.UTC(new Date().getFullYear(), Number(new Date().getMonth()) + 1, 15))),
        objet: t('NETTOYAGE DU MOIS DE ')
    }
    const [payload, setPayload, set] = useObjectState(defaultPayload)
    const api = useApi()

    const generate = async () => {
        const actionPayload = payload
        closePopup()
        await api.post(`/factures/generate-draft`, actionPayload)
        window.location.reload()
    }

    const closePopup = () => {
        onChange(null)
        setPayload(defaultPayload)
    }

    return <Popup value={!!value} onChange={value => onChange(value)}>
        <h2 className="font-bold text-lg mb-3">{t('Générer factures proforma')}</h2>
        <Form onSubmit={generate} className="flex flex-col gap-3">
            <Row>
                <DateInput className="grow" label={t("Date")} value={payload.date} onChange={set('date')} required />
                <DateInput className="grow" label={t("Date d'échéance")} value={payload.dateEcheance} onChange={set('dateEcheance')} required />
            </Row>
            <TextInput label={t("Objet de la facture")} value={payload.objet} onChange={set('objet')} />
            <Row className="mt-5">
                <Button gray icon={<FaBan />} onClick={closePopup}>{t('Cancel')}</Button>
                <Button submit icon={<FaSave />}>{t('Save')}</Button>
            </Row>
        </Form>
    </Popup>

}

function SendEmailPopup({ value, onChange, reload }) {
    const [factures, setFactures] = useState([])
    const [loading, setLoading] = useState(false)
    const [current, setCurrent] = useState(0)
    const api = useApi()
    const t = useTranslations()

    useEffect(() => {
        if (value) {
            setFactures([])
            setLoading(false)
            setCurrent(0)
            api.get(`/factures/monthly-emails`).then(setFactures)
        }
    }, [value])
    const submit = async () => {
        setLoading(true)
        for (const facture of factures) {
            setCurrent(factures.indexOf(facture))
            await api.post(`/factures/${facture.id}/send-email`)
        }
        setLoading(false)
        onChange(false)
        reload()
    }
    return <Popup value={value} onChange={onChange} large>
        <h2 className="font-bold text-lg mb-3">{t('Envoyer les factures par email')}</h2>
        {loading
            ? <Loading>{current + 1} / {factures.length}</Loading>
            : <>
                <div className="flex justify-center max-h-[300px] overflow-y-auto">
                    <table className="border-collapse">
                        <tr>
                            <th className="px-2 py-1 border border-black">{t('Exercice')}</th>
                            <th className="px-2 py-1 border border-black">{t('Numero')}</th>
                            <th className="px-2 py-1 border border-black">{t('Client')}</th>
                            <th className="px-2 py-1 border border-black">{t('Destinataire(s)')}</th>
                        </tr>
                        {factures.map(facture => <tr>
                            <td className=" px-2 py-1 border border-black">{facture.exercice}</td>
                            <td className=" px-2 py-1 border border-black">{facture.num}</td>
                            <td className=" px-2 py-1 border border-black">{facture.client}</td>
                            <td className=" px-2 py-1 border border-black">{facture.to.split('; ').map(x => <div>{x}</div>)}</td>
                        </tr>)}
                    </table>
                </div>
                <div className="flex items-center gap-5 mt-5">
                    <Button gray icon={<FaBan />} onClick={() => onChange(false)}>{t('Cancel')}</Button>
                    <Button icon={<FaPaperPlane />} onClick={submit}>{t('Envoyer')}</Button>
                </div>
            </>
        }
    </Popup>
}

export function SendSingleEmailPopup({ value, onChange, model, item, reload }) {
    const [loading, setLoading] = useState(false)
    const statusEnvoiEmailOptions = useStatusEnvoiEmailOptions()
    const api = useApi()
    const t = useTranslations()

    const submit = async () => {
        setLoading(true)
        await api.post(`/${model}/${item.id}/send-email`)
        onChange(false)
        setLoading(false)
        reload()
    }

    if (!item) return
    return <Popup value={value} onChange={onChange}>
        <h2 className="font-bold text-lg mb-3">{t('Envoyer email')}</h2>
        {item.draft ?
            <div>{t("Vous ne pouvez pas envoyer une facture proforma par email.")}</div>
            : loading
                ? <Loading />
                : <>
                    <div className="flex gap-2">
                        <div>{t("Vous allez envoyer cet email qui est pour l'instant dans le status:")}</div>
                        <Badge value={item.statusEnvoiEmail} options={statusEnvoiEmailOptions} />
                    </div>
                    <div className="flex items-center gap-5 mt-5">
                        <Button gray icon={<FaBan />} onClick={() => onChange(false)}>{t('Cancel')}</Button>
                        <Button icon={<FaPaperPlane />} onClick={submit}>{t('Envoyer')}</Button>
                    </div>
                </>}
    </Popup>
}

function ImportUnpaidInvoicesPopup({ value, onChange, reload }) {
    const [payload, setPayload, set] = useObjectState({})
    const api = useApi()
    const t = useTranslations()

    useEffect(() => {
        if (value) setPayload({})
    }, [value])
    const submit = async () => {
        await api.post('/factures/import-unpaid', payload)
        onChange(false)
        setPayload({})
        reload()
    }
    return <Popup value={value} onChange={onChange} large>
        <h2 className="font-bold text-lg mb-3">{t('Importer les factures non payées')}</h2>
        <Form onSubmit={submit} className="flex flex-col gap-3">
            <FileInput label={t('Fichier')} value={payload.file} onChange={set('file')} required accept="text/csv" />
            <div className="flex items-center gap-5 mt-5">
                <Button gray icon={<FaBan />} onClick={() => onChange(false)}>{t('Cancel')}</Button>
                <Button icon={<FaFileImport />} onClick={submit}>{t('Envoyer')}</Button>
            </div>
        </Form>
    </Popup>
}

function SendFirstRemindersPopup({ value, onChange, reload }) {
    const [emails, setEmails] = useState([])
    const [loading, setLoading] = useState(false)
    const [current, setCurrent] = useState(0)
    const api = useApi()
    const t = useTranslations()

    useEffect(() => {
        if (value) {
            setEmails([])
            setLoading(false)
            setCurrent(0)
            api.get(`/factures/first-reminders`).then(setEmails)
        }
    }, [value])

    const submit = async () => {
        setLoading(true)
        for (const email of emails) {
            setCurrent(emails.indexOf(email))
            await api.post(`/factures/send-first-reminder/${email.client.id}`)
        }
        setLoading(false)
        onChange(false)
        reload()
    }
    return <Popup value={value} onChange={onChange} large>
        <h2 className="font-bold text-lg mb-3">{t('Envoyer les premiers rappels par email')}</h2>
        {loading
            ? <Loading>{current + 1} / {emails.length}</Loading>
            : <>
                <div className="flex justify-center max-h-[300px] overflow-y-auto">
                    <table className="border-collapse">
                        <thead>
                            <tr>
                                <th className="px-2 py-1 border border-black">{t('Référence')}</th>
                                <th className="px-2 py-1 border border-black">{t('Nom')}</th>
                                <th className="px-2 py-1 border border-black">{t('Email')}</th>
                                <th className="px-2 py-1 border border-black">{t('CC')}</th>
                                <th className="px-2 py-1 border border-black">{t('Facture(s)')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {emails.map(email => <tr>
                                <td className="px-2 py-1 border border-black"><FkLink to={`/clients/${email.client.id}`}>{email.client.referenceCourte}</FkLink></td>
                                <td className="px-2 py-1 border border-black">{email.client.nom}</td>
                                <td className="px-2 py-1 border border-black">{email.email}</td>
                                <td className="px-2 py-1 border border-black">{email.cc.map(contact => <FkLink to={contact.gerantId ? `/gerants/${contact.gerantId}/contacts/${contact.id}` : `/clients/${contact.clientId}/contacts/${contact.id}`}>{contact.email}</FkLink>)}</td>
                                <td className="px-2 py-1 border border-black">{email.factures.map(facture => <FkLink to={`/factures/${facture.id}`}>{facture.exercice} - {facture.num}</FkLink>)}</td>
                            </tr>)}
                        </tbody>
                    </table>
                </div>
                <div className="flex items-center gap-5 mt-5">
                    <Button gray icon={<FaBan />} onClick={() => onChange(false)}>{t('Cancel')}</Button>
                    <Button icon={<FaPaperPlane />} onClick={submit}>{t('Envoyer')}</Button>
                </div>
            </>
        }
    </Popup>
}
function SendLastRemindersPopup({ value, onChange, reload }) {
    const [emails, setEmails] = useState([])
    const [loading, setLoading] = useState(false)
    const [current, setCurrent] = useState(0)
    const api = useApi()
    const t = useTranslations()

    useEffect(() => {
        if (value) {
            setEmails([])
            setLoading(false)
            setCurrent(0)
            api.get(`/factures/second-reminders`).then(setEmails)
        }
    }, [value])

    const submit = async () => {
        setLoading(true)
        for (const email of emails) {
            setCurrent(emails.indexOf(email))
            await api.post(`/factures/send-second-reminder/${email.client.id}`)
        }
        setLoading(false)
        onChange(false)
        reload()
    }
    return <Popup value={value} onChange={onChange} large>
        <h2 className="font-bold text-lg mb-3">{t('Envoyer les derniers rappels par email')}</h2>
        {loading
            ? <Loading>{current + 1} / {emails.length}</Loading>
            : <>
                <div className="flex justify-center max-h-[300px] overflow-y-auto">
                    <table className="border-collapse">
                        <thead>
                            <tr>
                                <th className="px-2 py-1 border border-black">{t('Référence')}</th>
                                <th className="px-2 py-1 border border-black">{t('Nom')}</th>
                                <th className="px-2 py-1 border border-black">{t('Email')}</th>
                                <th className="px-2 py-1 border border-black">{t('CC')}</th>
                                <th className="px-2 py-1 border border-black">{t('Facture(s) & date 1er rappel')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {emails.map(email => <tr>
                                <td className="px-2 py-1 border border-black"><FkLink to={`/clients/${email.client.id}`}>{email.client.referenceCourte}</FkLink></td>
                                <td className="px-2 py-1 border border-black">{email.client.nom}</td>
                                <td className="px-2 py-1 border border-black">{email.email}</td>
                                <td className="px-2 py-1 border border-black">{email.cc.map(contact => <FkLink to={contact.gerantId ? `/gerants/${contact.gerantId}/contacts/${contact.id}` : `/clients/${contact.clientId}/contacts/${contact.id}`}>{contact.email}</FkLink>)}</td>
                                <td className="px-2 py-1 border border-black">{email.factures.map(facture => <FkLink to={`/factures/${facture.id}`}>{facture.exercice} - {facture.num} ({facture.datePremierRappel.toLocaleDateString()})</FkLink>)}</td>
                            </tr>)}
                        </tbody>
                    </table>
                </div>
                <div className="flex items-center gap-5 mt-5">
                    <Button gray icon={<FaBan />} onClick={() => onChange(false)}>{t('Cancel')}</Button>
                    <Button icon={<FaPaperPlane />} onClick={submit}>{t('Envoyer')}</Button>
                </div>
            </>
        }
    </Popup>
}

export function FacturesControle() {
    const [loading, setLoading] = useState(true)
    const [clients, setClients] = useState([])
    const [openClient, setOpenClient] = useState()
    const [openClientSize, setOpenClientSize] = useState()
    const api = useApi()
    const t = useTranslations()
    const today = useToday()
    const codesTvaOptions = useCodesTvaOptions()

    useEffect(() => {
        api.get(`/factures/controle?date=${today.toISOString()}`).then(setClients).then(() => setLoading(false))

    }, [today])

    const clientToMonthTotal = (client, month) => {
        return (
            client.factures
                .filter(x => x.date.getUTCMonth() === month)
                .flatMap(x => x.facturesDetail)
                .reduce((total, x) => total + x.prixUnitaire * x.quantite * (1 + codesTvaOptions.find(codeTvaOption => x.codeTva === codeTvaOption.value).taux / 100), 0)
            - client.notesDeCredit
                .filter(x => x.date.getUTCMonth() === month)
                .flatMap(x => x.notesDeCreditDetail)
                .reduce((total, x) => total + x.prixUnitaire * x.quantite * (1 + codesTvaOptions.find(codeTvaOption => x.codeTva === codeTvaOption.value).taux / 100), 0)
        )
    }
    const clientToMonthTotalLabel = (client, month) => {
        if (
            (
                client.datePremierTravail
                && client.datePremierTravail >= new Date(Date.UTC(today.getUTCFullYear(), month + 1, 1))
            )
            || (
                client.dateDernierTravail &&
                client.dateDernierTravail < new Date(Date.UTC(today.getUTCFullYear(), month, 1))
            )
        ) return "/"
        return toCurrency(clientToMonthTotal(client, month))
    }
    const capitalize = string => string[0].toUpperCase() + string.slice(1)
    const toggleClient = (e, client) => {
        setOpenClient(client.id === openClient?.id ? null : client)
        setOpenClientSize(e.target.parentElement.nextElementSibling.scrollHeight + 'px')
    }

    return <div className="grow w-full">
        <h1 className="text-2xl font-bold mb-3">{t('Contrôle de la facturation')}</h1>
        {loading
            ? <Loading />
            : <div className="overflow-auto border border-gray-300 bg-white rounded-xl">
                <div className="grid grid-cols-[repeat(10,auto)]">
                    <div className="px-3 py-2 first:pl-5 last:pr-5 text-left bg-gray-200 font-bold">{t('Numéro')}</div>
                    <div className="px-3 py-2 first:pl-5 last:pr-5 text-left bg-gray-200 font-bold">{t('Référence courte')}</div>
                    <div className="px-3 py-2 first:pl-5 last:pr-5 text-left bg-gray-200 font-bold">{t('Nom')}</div>
                    <div className="px-3 py-2 first:pl-5 last:pr-5 text-left bg-gray-200 font-bold">{t('Dates de travail')}</div>
                    {[-5, -4, -3, -2, -1, 0].map(month =>
                        <div key={month} className="px-3 py-2 first:pl-5 last:pr-5 text-left bg-gray-200 font-bold">{capitalize(new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() + month, 1)).toLocaleDateString(undefined, { month: 'long' }))}</div>
                    )}
                    {clients.map(client => <div key={client.id} className="contents">
                        <div className="contents group" onClick={e => toggleClient(e, client)}>
                            <div className="px-3 py-2 first:pl-5 last:pr-5 border-t border-gray-300 cursor-pointer group-hover:bg-gray-200 whitespace-nowrap md:whitespace-normal">{client.num}</div>
                            <div className="px-3 py-2 first:pl-5 last:pr-5 border-t border-gray-300 cursor-pointer group-hover:bg-gray-200 whitespace-nowrap md:whitespace-normal">{client.referenceCourte}</div>
                            <div className="px-3 py-2 first:pl-5 last:pr-5 border-t border-gray-300 cursor-pointer group-hover:bg-gray-200 whitespace-nowrap md:whitespace-normal">{client.nom}</div>
                            <div className="px-3 py-2 first:pl-5 last:pr-5 border-t border-gray-300 cursor-pointer group-hover:bg-gray-200 whitespace-nowrap md:whitespace-normal">{client.datePremierTravail ? client.datePremierTravail.toLocaleDateString() : ""} - {client.dateDernierTravail ? <span><br />{client.dateDernierTravail.toLocaleDateString()}</span> : "..."}</div>
                            {[-5, -4, -3, -2, -1, 0].map(x => (x + today.getMonth() + 12) % 12).map(columnMonth =>
                                <div key={columnMonth} className={`px-3 py-2 first:pl-5 last:pr-5 border-t border-gray-300 cursor-pointer group-hover:bg-gray-200 whitespace-nowrap md:whitespace-normal ${columnMonth === today.getUTCMonth() || (columnMonth === today.getUTCMonth() - 1 && clientToMonthTotalLabel(client, today.getUTCMonth()) !== clientToMonthTotalLabel(client, today.getUTCMonth() - 1)) ? 'font-bold' : ''} ${(columnMonth === today.getUTCMonth() || columnMonth === today.getUTCMonth() - 1) && clientToMonthTotalLabel(client, today.getUTCMonth()) !== clientToMonthTotalLabel(client, today.getUTCMonth() - 1) ? 'text-red-500' : ''}`}>
                                    {clientToMonthTotalLabel(client, columnMonth)}
                                </div>
                            )}
                        </div>
                        <div className="col-span-10 transition-all overflow-hidden" style={{ maxHeight: client.id === openClient?.id ? openClientSize : 0 }}>
                            <div className="p-5 border-t border-dashed border-gray-300">
                                <div className="overflow-auto border border-gray-300 rounded-lg">
                                    <div className="grid grid-cols-[repeat(10,auto)] text-base">
                                        <div className="px-2 py-1 font-bold bg-gray-200">{t('Type')}</div>
                                        <div className="px-2 py-1 font-bold bg-gray-200">{t('Num')}</div>
                                        <div className="px-2 py-1 font-bold bg-gray-200">{t('Date')}</div>
                                        <div className="px-2 py-1 font-bold bg-gray-200">{t('Nom')}</div>
                                        <div className="px-2 py-1 font-bold bg-gray-200">{t('Prix/u')}</div>
                                        <div className="px-2 py-1 font-bold bg-gray-200">{t('Qte')}</div>
                                        <div className="px-2 py-1 font-bold bg-gray-200">{t('HTVA')}</div>
                                        <div className="px-2 py-1 font-bold bg-gray-200">{t('Taux')}</div>
                                        <div className="px-2 py-1 font-bold bg-gray-200">{t('TVA')}</div>
                                        <div className="px-2 py-1 font-bold bg-gray-200">{t('TVAC')}</div>
                                        {[
                                            ...client.factures.flatMap(x => x.facturesDetail.map(detail => ({ ...detail, facture: x }))),
                                            ...client.notesDeCredit.flatMap(x => x.notesDeCreditDetail.map(detail => ({ ...detail, noteDeCredit: x }))),
                                        ].sort((a, b) => (
                                            (a.facture || a.noteDeCredit).date.getTime() !== (b.facture || b.noteDeCredit).date.getTime()
                                                ? (b.facture || b.noteDeCredit).date.getTime() - (a.facture || a.noteDeCredit).date.getTime()
                                                : (a.facture || a.noteDeCredit).num !== (b.facture || b.noteDeCredit).num
                                                    ? (a.facture || a.noteDeCredit).num - (b.facture || b.noteDeCredit).num
                                                    : (a.facture || a.noteDeCredit).ordre - (b.facture || b.noteDeCredit).ordre
                                        ))
                                            .map(detail => <div className="contents">
                                                <div className="px-2 py-1 border-t border-gray-300">{detail.noteDeCredit ? t('Note de crédit') : detail.facture.draft ? t('Proforma') : t('Facture')}</div>
                                                <div className="px-2 py-1 border-t border-gray-300">{(detail.facture || detail.noteDeCredit).num}</div>
                                                <div className="px-2 py-1 border-t border-gray-300">{(detail.facture || detail.noteDeCredit).date.toLocaleDateString()}</div>
                                                <div className="px-2 py-1 border-t border-gray-300">{detail.nom}</div>
                                                <div className="px-2 py-1 border-t border-gray-300">{toCurrency(detail.prixUnitaire)}</div>
                                                <div className="px-2 py-1 border-t border-gray-300">{detail.quantite}</div>
                                                <div className="px-2 py-1 border-t border-gray-300">{toCurrency(detail.prixUnitaire * detail.quantite)}</div>
                                                <div className="px-2 py-1 border-t border-gray-300">{codesTvaOptions.find(x => x.value === detail.codeTva).label}</div>
                                                <div className="px-2 py-1 border-t border-gray-300">{toCurrency(detail.prixUnitaire * detail.quantite * codesTvaOptions.find(x => x.value === detail.codeTva).taux / 100)}</div>
                                                <div className="px-2 py-1 border-t border-gray-300 font-bold">{toCurrency(detail.prixUnitaire * detail.quantite * (1 + codesTvaOptions.find(x => x.value === detail.codeTva).taux / 100))}</div>
                                            </div>)
                                        }
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>)}
                </div>
            </div>}
    </div>
}